import type { IconType } from '@kognitos/lattice';

/**
 * A file the agent wrote during a sandbox turn, as the chat card and the
 * viewer need it. Derived from an `artifact_write` / `artifact_edit` tool call
 * — the URI is the handle the content endpoint takes, the rest is display.
 */
export interface IAgentArtifactFile {
  /** Full artifact URI, e.g. `scratch://review-report.html`. Sent to the API. */
  uri: string;
  /** Last path segment, e.g. `review-report.html`. What the user reads. */
  filename: string;
  /** Lowercase extension without the dot, e.g. `html`. Empty when there is none. */
  extension: string;
  /** Committed size in bytes, off the tool result. Absent while still writing. */
  sizeBytes?: number;
  /** The agent's own one-line note about the file, when it passed one. */
  description?: string;
}

/**
 * Splits an artifact URI into its store and path — `scratch://review-report.html`
 * becomes `['scratch', 'review-report.html']`. A URI with no `://` is treated as
 * a bare path so a malformed one still yields a usable filename.
 */
const splitArtifactUri = (uri: string): { path: string } => {
  const separator = uri.indexOf('://');
  return { path: separator === -1 ? uri : uri.slice(separator + 3) };
};

/**
 * Human filename for an artifact URI.
 *
 * Bishop's workspace store mangles names into `{id}--{filename}` (see the
 * `artifact_copy` results in a real thread:
 * `workspace://lUd-oggh-pHRNAyW5W2Ne--S29nbml0b3MgVGVzdCBNU0EgMQ.Pdf`), so the
 * id prefix is stripped when present. Every other store keeps the name as
 * written.
 */
export const getArtifactFilename = (uri: string): string => {
  const { path } = splitArtifactUri(uri);
  const lastSegment = path.split('/').filter(Boolean).pop() ?? path;
  const decoded = safeDecode(lastSegment);
  const bishopSeparator = decoded.indexOf('--');
  return bishopSeparator === -1
    ? decoded
    : decoded.slice(bishopSeparator + 2) || decoded;
};

/**
 * `decodeURIComponent` throws on a malformed escape (a lone `%`), which a
 * filename the agent invented can contain. A name we cannot decode is still a
 * better label than a crash.
 */
const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** Lowercase extension without the dot; empty string when the name has none. */
export const getArtifactExtension = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
};

/**
 * Extension -> icon. Grouped by what the user is about to see rather than by
 * MIME family, so a `.md` report and a `.txt` note share the document icon
 * while `.html` sits with code (it renders as a page, but it is authored
 * markup and the distinction helps scanning a list of agent outputs).
 */
const EXTENSION_ICONS: Readonly<Record<string, IconType>> = {
  csv: 'FileSpreadsheet',
  tsv: 'FileSpreadsheet',
  xls: 'FileSpreadsheet',
  xlsx: 'FileSpreadsheet',
  json: 'FileJson',
  html: 'FileCode',
  htm: 'FileCode',
  xml: 'FileCode',
  yaml: 'FileCode',
  yml: 'FileCode',
  py: 'FileCode',
  js: 'FileCode',
  ts: 'FileCode',
  sql: 'FileCode',
  pdf: 'FileText',
  doc: 'FileText',
  docx: 'FileText',
  md: 'FileText',
  txt: 'FileText',
  rtf: 'FileText',
  png: 'Image',
  jpg: 'Image',
  jpeg: 'Image',
  gif: 'Image',
  webp: 'Image',
  svg: 'Image',
};

/** Icon for a file's extension, falling back to the generic file glyph. */
export const getArtifactFileIcon = (extension: string): IconType =>
  EXTENSION_ICONS[extension] ?? 'File';

/**
 * Short type label shown beside the size, e.g. `HTML`. Uppercased extension
 * rather than a MIME string: `HTML · 6.2 KB` reads at a glance where
 * `text/html · 6.2 KB` does not.
 */
export const getArtifactTypeLabel = (extension: string): string =>
  extension === '' ? '' : extension.toUpperCase();

// There is deliberately no extension -> MIME map here. The viewer hands
// `PreviewLayover` a `file-inline` value, whose shape carries no media type,
// and the file renderer identifies the format by sniffing magic bytes — so an
// extension-derived guess has nowhere to go and nothing to decide.
